import { Card, CardContent, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

export type StatSeverity = "default" | "success" | "warning" | "critical" | "info";

const VALUE_COLOR: Record<StatSeverity, string> = {
  default: "text.primary",
  info: "primary.main",
  success: "success.main",
  warning: "warning.main",
  critical: "error.main",
};

/**
 * KPI tile for the summary row at the top of a screen — uppercase label, one big tabular value, an
 * optional hint. Severity tints the value only (never the whole card). Clickable when `onClick` is set.
 */
export function StatCard({
  label, value, hint, severity = "default", onClick,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  severity?: StatSeverity;
  onClick?: () => void;
}) {
  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        flex: "1 1 180px",
        minWidth: 160,
        cursor: onClick ? "pointer" : "default",
        transition: "border-color 120ms, background-color 120ms",
        ...(onClick && { "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" } }),
      }}
    >
      <CardContent sx={{ py: 1.75, "&:last-child": { pb: 1.75 } }}>
        <Stack spacing={0.75}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}
          >
            {label}
          </Typography>
          <Typography variant="h5" sx={{ color: VALUE_COLOR[severity], fontWeight: 700 }}>
            {value}
          </Typography>
          {hint && (
            <Typography variant="caption" color="text.secondary">
              {hint}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
